import { Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { NotificationsBell } from './NotificationsBell';
import { UserButton } from '@/components/auth/UserButton';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useTranslation } from 'react-i18next';

interface TopbarProps {
  onMenuClick: () => void;
}

// Claves de i18n, no textos: el componente traduce con t(). El sidebar ya salía
// traducido (useVisibleSections) y el header seguía en español.
const ROUTE_LABEL_KEYS: Record<string, string> = {
  '/dashboard': 'layout.topbar.routes.dashboard',
  '/skills-agentes': 'layout.topbar.routes.skills_agentes',
  '/tickets/select': 'layout.topbar.routes.tickets_select',
  '/tickets/mine': 'layout.topbar.routes.tickets_mine',
  '/admin/tickets': 'layout.topbar.routes.admin_tickets',
  '/admin/inventory': 'layout.topbar.routes.admin_inventory',
  '/admin/devices/approval': 'layout.topbar.routes.admin_devices_approval',
  '/admin/devices/useful-info': 'layout.topbar.routes.admin_devices_useful_info',
  '/admin/users': 'layout.topbar.routes.admin_users',
  '/admin/users/people-panel': 'layout.topbar.routes.admin_users_people_panel',
  '/admin/users/pedidos': 'layout.topbar.routes.admin_users_pedidos',
  '/admin/users/oficinas': 'layout.topbar.routes.admin_users_oficinas',
  '/admin/devices/in-out': 'layout.topbar.routes.admin_devices_in_out',
  '/people/landing': 'layout.topbar.routes.people_landing',
  '/people/learning': 'layout.topbar.routes.people_learning',
  '/people/talent-acquisition': 'layout.topbar.routes.people_talent_acquisition',
  '/people/learning/arcade': 'layout.topbar.routes.people_learning_arcade',
  '/finance/pricing': 'layout.topbar.routes.finance_pricing',
  '/brain': 'layout.topbar.routes.brain',
  '/partnerships': 'layout.topbar.routes.partnerships',
  '/admin/users/community-map': 'layout.topbar.routes.admin_users_community_map',
  '/ia-native': 'layout.topbar.routes.ia_native',
  '/ia-native/subscriptions': 'layout.topbar.routes.ia_native_subscriptions',
  '/technology/finops': 'layout.topbar.routes.technology_finops',
  '/technology/finops/alerts': 'layout.topbar.routes.technology_finops_alerts',
  '/performance': 'layout.topbar.routes.performance',
  '/performance/talent-map': 'layout.topbar.routes.performance_talent_map',
  '/performance/dtalk': 'layout.topbar.routes.performance_dtalk',
  '/performance/history': 'layout.topbar.routes.performance_history',
  '/performance/admin': 'layout.topbar.routes.performance_admin',
  '/profile': 'layout.topbar.routes.profile',
};

function getPageLabelKey(pathname: string): string | null {
  // Exact match
  if (ROUTE_LABEL_KEYS[pathname]) return ROUTE_LABEL_KEYS[pathname];
  // Longest-prefix match (for dynamic/nested routes)
  let best: { route: string; key: string } | null = null;
  for (const [route, key] of Object.entries(ROUTE_LABEL_KEYS)) {
    if (pathname.startsWith(route + '/') && (!best || route.length > best.route.length)) {
      best = { route, key };
    }
  }
  return best?.key ?? null;
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const pageLabelKey = getPageLabelKey(location.pathname);
  const pageLabel = pageLabelKey ? t(pageLabelKey) : 'Winclap';

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background px-4 sm:px-6">
      <div className="flex items-center gap-4">
        <button
          className="md:hidden rounded-md p-1.5 text-muted-foreground hover:bg-muted transition-colors"
          onClick={onMenuClick}
          aria-label={t('layout.topbar.abrir_menu')}
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="font-display text-lg font-normal text-foreground tracking-tight">
          {pageLabel}
        </h1>
      </div>

      <div className="flex items-center gap-2">
        <LanguageSwitcher />
        <ThemeToggle />
        <NotificationsBell />
        <UserButton />
      </div>
    </header>
  );
}
